'use client';

import { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import api from '@/lib/api';
import { useScratchpadApplyTarget } from '@/lib/scratchpad';
import { aiModelsApi } from '@/lib/ai-models-api';
import {
  JobResponse, QueueStats, HealthResponse, JobCreate, BatchJobCreate, Series,
  PromptBit, PromptBitCategory, AgentBuildOp,
} from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import BuilderForm from '@/components/builder/BuilderForm';
import BitLibraryPanel from '@/components/builder/BitLibraryPanel';
import AgentChatPanel, { LIBRARY_WRITE_TOOLS } from '@/components/builder/AgentChatPanel';
import BuildsMenu from '@/components/builder/BuildsMenu';
import JobCard from '@/components/JobCard';
import StatusBar from '@/components/StatusBar';
import ImageModal from '@/components/ImageModal';
import { appendSection, getSectionText, parseSections, replaceSectionText, stripComments } from '@/lib/promptSections';

const FORM_ID = 'builder-gen-form';

// Debounce helper (cancel() clears the pending timer, e.g. on unmount)
function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  return Object.assign(debounced, {
    cancel: () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    },
  });
}

const toDataUri = (img: string) => (img.startsWith('data:') ? img : `data:image/png;base64,${img}`);

// Insert rule (plan §4): absent section → append at its sort position; otherwise a click
// replaces — targeting the focused ribbon chip when it matches the bit's category and still
// exists, else the first empty occurrence (e.g. freshly created via the ribbon's "+ Add"),
// else occurrence 0. Appending extra occurrences is reserved for the explicit "+ Add".
function insertBitText(
  raw: string,
  category: PromptBitCategory,
  text: string,
  orderedSlugs: string[],
  focused?: { slug: string; occurrence: number } | null
): string {
  const sections = parseSections(raw).filter(s => s.slug === category.slug);
  if (sections.length === 0) {
    return appendSection(raw, category.slug, text, orderedSlugs);
  }
  if (
    focused &&
    focused.slug === category.slug &&
    sections.some(s => s.occurrence === focused.occurrence)
  ) {
    return replaceSectionText(raw, category.slug, focused.occurrence, text);
  }
  const target = sections.find(s => s.text.trim() === '') ?? sections[0];
  return replaceSectionText(raw, category.slug, target.occurrence, text);
}

// Swap-mode rule: deterministic replace — the focused occurrence when it matches the bit's
// category and still exists, else occurrence 0. An absent section is appended at its sort
// position; extra occurrences are NEVER added in swap mode.
function swapBitText(
  raw: string,
  category: PromptBitCategory,
  text: string,
  orderedSlugs: string[],
  focused?: { slug: string; occurrence: number } | null
): string {
  const sections = parseSections(raw).filter(s => s.slug === category.slug);
  if (sections.length === 0) {
    return appendSection(raw, category.slug, text, orderedSlugs);
  }
  const occurrence =
    focused &&
    focused.slug === category.slug &&
    sections.some(s => s.occurrence === focused.occurrence)
      ? focused.occurrence
      : 0;
  return replaceSectionText(raw, category.slug, occurrence, text);
}

// Apply agent build ops to the raw prompt — the single op-application path, shared by the
// AgentChatPanel wiring and the cast workflow.
function applyBuildOps(raw: string, ops: AgentBuildOp[], orderedSlugs: string[]): string {
  let next = raw;
  for (const op of ops) {
    const text = op.text ?? '';
    const index = op.index ?? 0;
    // set_section with an index beyond existing occurrences behaves like append
    if (op.op === 'append_section' || getSectionText(next, op.category, index) === null) {
      next = appendSection(next, op.category, text, orderedSlugs);
    } else {
      next = replaceSectionText(next, op.category, index, text);
    }
  }
  return next;
}

export default function XplorePage() {
  return (
    <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
      <XploreContent />
    </Suspense>
  );
}

function XploreContent() {
  const searchParams = useSearchParams();

  // Data state (same as Collage page)
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const jobsRef = useRef<JobResponse[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Partial<JobCreate>>({});
  const [series, setSeries] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Prompt bit library
  const [categories, setCategories] = useState<PromptBitCategory[]>([]);
  const [bits, setBits] = useState<PromptBit[]>([]);

  // Left column tab (both panels stay mounted so the agent chat survives tab switches)
  const [leftTab, setLeftTab] = useState<'library' | 'agent'>('library');

  // Below lg the asides become overlay drawers toggled from the header (null = closed)
  const [mobilePanel, setMobilePanel] = useState<'library' | 'settings' | null>(null);

  // Last ribbon chip the user clicked/focused — bit inserts target that occurrence.
  // Mirrored in state so activeBitIds (swap-mode highlight) recomputes on focus change;
  // the ref stays authoritative inside setPrompt updaters.
  const focusedSectionRef = useRef<{ slug: string; occurrence: number } | null>(null);
  const [focusedSection, setFocusedSection] = useState<{ slug: string; occurrence: number } | null>(null);
  const handleSectionFocus = useCallback((slug: string, occurrence: number) => {
    focusedSectionRef.current = { slug, occurrence };
    setFocusedSection({ slug, occurrence });
  }, []);

  // Host element in the right sidebar the form portals its settings panel into
  const [settingsHost, setSettingsHost] = useState<HTMLDivElement | null>(null);

  // Controlled prompt state — single source of truth for the builder
  const [prompt, setPrompt] = useState('');

  // Offer this prompt field to the scratchpad's Apply action.
  useScratchpadApplyTarget(setPrompt);
  const promptRef = useRef(prompt);
  promptRef.current = prompt;

  // Dimensions and seed from the form (for preview)
  const [dimensions, setDimensions] = useState({ width: 1024, height: 1024 });
  const [previewSeed, setPreviewSeed] = useState<number | null>(null);

  // Memoized callbacks to prevent infinite loop
  const handleDimensionsChange = useCallback((w: number, h: number) => {
    setDimensions(prev => {
      if (prev.width === w && prev.height === h) return prev;
      return { width: w, height: h };
    });
  }, []);

  const handleSeedChange = useCallback((seed: number | null) => {
    setPreviewSeed(prev => prev === seed ? prev : seed);
  }, []);

  // Preview state for the floating window
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDimensions, setPreviewDimensions] = useState({ width: 512, height: 512 });

  // Floating preview state (opt-in overlay; default off, nothing persisted)
  const [floatingPreviewOpen, setFloatingPreviewOpen] = useState(false);
  const [floatingPos, setFloatingPos] = useState({ x: -1, y: 80 }); // -1 signals "use default"
  const [floatingWidth, setFloatingWidth] = useState(512);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ mouseX: 0, mouseY: 0, width: 512 });

  // Throttle refs for preview generation
  const lastGenerationTimeRef = useRef(0);
  const pendingGenerationRef = useRef<{ prompt: string; width: number; height: number; seed: number | null } | null>(null);
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Monotonic request id — only the latest in-flight preview may apply its result
  const previewReqIdRef = useRef(0);

  // Initialize floating position on mount
  useEffect(() => {
    if (floatingPos.x === -1 && typeof window !== 'undefined') {
      setFloatingPos({ x: window.innerWidth - floatingWidth - 36, y: 80 });
    }
  }, [floatingPos.x, floatingWidth]);

  // Drag handling for floating window
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setFloatingPos({
        x: e.clientX - dragOffsetRef.current.x,
        y: e.clientY - dragOffsetRef.current.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Resize handling for floating window
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartRef.current.mouseX;
      const newWidth = Math.max(200, Math.min(resizeStartRef.current.width + delta, previewDimensions.width));
      setFloatingWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, previewDimensions.width]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, jobsRes, seriesRes] = await Promise.all([
        api.getHealth(),
        api.getQueueStats(),
        api.listJobs(undefined, 500, 0, selectedSeriesId ?? undefined),
        api.getSeries(),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setJobs(jobsRes);
      jobsRef.current = jobsRes;
      setSeries(seriesRes);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [selectedSeriesId]);

  // Library data is fetched separately from the 2s job poll: with thousands of
  // bits the payload is large, and it only changes on explicit library actions.
  const fetchLibrary = useCallback(async () => {
    try {
      const [categoriesRes, bitsRes] = await Promise.all([
        api.getPromptBitCategories(),
        // include_inactive so the library can show and re-activate disabled bits
        api.getPromptBits({ includeInactive: true }),
      ]);
      setCategories(categoriesRes);
      setBits(bitsRes);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    }
  }, []);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = jobsRef.current.some(
        j => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Apply URL parameters on mount (for "Use in ..." links from other pages)
  useEffect(() => {
    if (urlParamsApplied) return;

    const promptParam = searchParams.get('prompt');
    const seriesIdParam = searchParams.get('series_id');
    const widthParam = searchParams.get('width');
    const heightParam = searchParams.get('height');
    const stepsParam = searchParams.get('steps');
    const cfgParam = searchParams.get('cfg');
    const seedParam = searchParams.get('seed');

    if (promptParam) {
      // searchParams.get already returns the percent-decoded value — no extra decode
      setPrompt(promptParam);

      const newFormValues: Partial<JobCreate> = {
        prompt: promptParam,
      };

      if (widthParam) newFormValues.width = parseInt(widthParam);
      if (heightParam) newFormValues.height = parseInt(heightParam);
      if (stepsParam) newFormValues.steps = parseInt(stepsParam);
      if (cfgParam) newFormValues.cfg = parseFloat(cfgParam);
      if (seedParam && seedParam !== 'null') newFormValues.seed = parseInt(seedParam);
      if (seriesIdParam) newFormValues.series_id = parseInt(seriesIdParam);

      setFormValues(newFormValues);

      if (seriesIdParam) {
        setSelectedSeriesId(parseInt(seriesIdParam));
      }

      setUrlParamsApplied(true);
      toast.success('Prompt loaded');
    }
  }, [searchParams, urlParamsApplied, toast]);

  // === Prompt bit library wiring ===

  const orderedSlugs = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order).map(c => c.slug),
    [categories]
  );

  // Active bits per category slug, sorted by sort_order (for section cycling in the form)
  const bitsBySlug = useMemo(() => {
    const slugById = new Map(categories.map(c => [c.id, c.slug]));
    const bySlug: Record<string, PromptBit[]> = {};
    for (const bit of bits) {
      if (!bit.is_active) continue;
      const slug = slugById.get(bit.category_id);
      if (!slug) continue;
      (bySlug[slug] = bySlug[slug] || []).push(bit);
    }
    for (const slug of Object.keys(bySlug)) {
      bySlug[slug].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    }
    return bySlug;
  }, [bits, categories]);

  const handleInsertBit = useCallback((bit: PromptBit, mode: 'build' | 'swap') => {
    const category = categories.find(c => c.id === bit.category_id);
    if (!category) return;
    const apply = mode === 'swap' ? swapBitText : insertBitText;
    setPrompt(prev => apply(prev, category, bit.text, orderedSlugs, focusedSectionRef.current));
  }, [categories, orderedSlugs]);

  // Bits currently "live" in the prompt — per category, the bit(s) whose trimmed text equals
  // the relevant section text (focused occurrence when its slug matches, else occurrence 0).
  // BitLibraryPanel ring-highlights these in swap mode.
  const activeBitIds = useMemo(() => {
    const ids = new Set<number>();
    for (const category of categories) {
      let text: string | null = null;
      if (focusedSection && focusedSection.slug === category.slug) {
        text = getSectionText(prompt, category.slug, focusedSection.occurrence);
      }
      if (text === null) text = getSectionText(prompt, category.slug, 0);
      if (text === null) continue;
      const trimmed = text.trim();
      if (!trimmed) continue;
      for (const bit of bits) {
        if (bit.category_id === category.id && bit.text.trim() === trimmed) {
          ids.add(bit.id);
        }
      }
    }
    return ids;
  }, [prompt, bits, categories, focusedSection]);

  const handleInsertInlineList = useCallback((categorySlug: string, texts: string[]) => {
    const inline = `{${texts.join('|')}}`;
    setPrompt(prev =>
      getSectionText(prev, categorySlug, 0) === null
        ? appendSection(prev, categorySlug, inline, orderedSlugs)
        : replaceSectionText(prev, categorySlug, 0, inline)
    );
  }, [orderedSlugs]);

  // === Agent wiring ===

  const getBuildState = useCallback(() => promptRef.current, []);

  const handleBuildOps = useCallback((ops: AgentBuildOp[]) => {
    setPrompt(prev => applyBuildOps(prev, ops, orderedSlugs));
  }, [orderedSlugs]);

  // === Cast workflow (Person bits) ===

  // Person's name while a cast is running (drives the "Casting <name>…" indicator)
  const [castingLabel, setCastingLabel] = useState<string | null>(null);
  // Concurrency guard — state can be stale inside the async callback
  const isCastingRef = useRef(false);

  const handleCastBit = useCallback(async (bit: PromptBit) => {
    if (isCastingRef.current) {
      toast.error('A cast is already in progress');
      return;
    }
    isCastingRef.current = true;
    setCastingLabel(bit.label);
    try {
      // Number of person sections BEFORE this cast — the appended section gets index n
      const n = parseSections(promptRef.current).filter(s => s.slug === 'person').length;
      // Append the person section via the same path bit inserts use
      const nextPrompt = appendSection(promptRef.current, 'person', bit.text, orderedSlugs);
      setPrompt(nextPrompt);

      const response = await api.agentChat({
        messages: [{
          role: 'user',
          content:
            `I just cast "${bit.text}" as character ${n + 1} (person section index ${n}). ` +
            `Fill in matching hair, clothing, expression and pose for this character using ` +
            `set_section/append_section with index ${n} — choices that suit this person's ` +
            `typical public look and fit the existing scene and other sections. Do not ` +
            `modify other characters' sections. Keep each section concise.`,
        }],
        build_state: nextPrompt,
      });

      if (response.build_ops.length > 0) {
        handleBuildOps(response.build_ops);
      }
      if (response.tool_events.some(ev => LIBRARY_WRITE_TOOLS.includes(ev.tool))) {
        fetchLibrary();
      }
      const filled = [...new Set(response.build_ops.map(op => op.category))];
      toast.success(
        filled.length > 0
          ? `Cast ${bit.label} — filled ${filled.join(', ')}`
          : `Cast ${bit.label}`
      );
    } catch (error) {
      toast.error('Cast failed: ' + (error as Error).message);
    } finally {
      isCastingRef.current = false;
      setCastingLabel(null);
    }
  }, [orderedSlugs, handleBuildOps, fetchLibrary, toast]);

  // === Preview generation (floating window) ===

  // Debounced preview generation with correct aspect ratio and seed
  const generatePreview = useCallback(async (promptText: string, width: number, height: number, seed: number | null) => {
    if (!promptText.trim()) return;

    const reqId = ++previewReqIdRef.current;
    // Measure the throttle gap from dispatch, so concurrent dispatches are actually throttled
    lastGenerationTimeRef.current = Date.now();
    setPreviewLoading(true);

    try {
      const baseSize = 512;
      const aspectRatio = width / height;
      let previewWidth: number;
      let previewHeight: number;

      // Round to nearest multiple of 16
      const roundTo16 = (n: number) => Math.round(n / 16) * 16;

      if (aspectRatio >= 1) {
        // Landscape or square: height is the smaller dimension
        previewHeight = baseSize;
        previewWidth = Math.max(baseSize, roundTo16(baseSize * aspectRatio));
      } else {
        // Portrait: width is the smaller dimension
        previewWidth = baseSize;
        previewHeight = Math.max(baseSize, roundTo16(baseSize / aspectRatio));
      }

      const result = await aiModelsApi.imagegenGenerate({
        prompt: stripComments(promptText),
        width: previewWidth,
        height: previewHeight,
        num_inference_steps: 4,
        seed: seed,
        model: 'z-image-turbo',
      });

      // Latest-wins: a stale response must not overwrite a newer preview
      if (reqId === previewReqIdRef.current) {
        setPreviewImage(result.image);
        setPreviewDimensions({ width: previewWidth, height: previewHeight });
      }
      lastGenerationTimeRef.current = Date.now();
    } catch (error) {
      console.error('Preview generation failed:', error);
    } finally {
      if (reqId === previewReqIdRef.current) {
        setPreviewLoading(false);
      }
    }
  }, []);

  // Throttled generation: enforces minimum 2s gap between preview generations
  const throttledGenerate = useCallback((promptText: string, width: number, height: number, seed: number | null) => {
    const now = Date.now();
    const elapsed = now - lastGenerationTimeRef.current;
    const minGap = 2000;

    if (elapsed >= minGap) {
      // Enough time passed, generate immediately
      generatePreview(promptText, width, height, seed);
    } else {
      // Queue the latest params and schedule generation after cooldown
      pendingGenerationRef.current = { prompt: promptText, width, height, seed };
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          throttleTimerRef.current = null;
          const pending = pendingGenerationRef.current;
          if (pending) {
            pendingGenerationRef.current = null;
            generatePreview(pending.prompt, pending.width, pending.height, pending.seed);
          }
        }, minGap - elapsed);
      }
    }
  }, [generatePreview]);

  // Clean up throttle timer
  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
      pendingGenerationRef.current = null;
    };
  }, []);

  const debouncedGeneratePreview = useMemo(
    () => debounce((promptText: string, width: number, height: number, seed: number | null) => throttledGenerate(promptText, width, height, seed), 800),
    [throttledGenerate]
  );

  // Cancel the pending debounce timer on unmount (and on stale instances)
  useEffect(() => () => debouncedGeneratePreview.cancel(), [debouncedGeneratePreview]);

  useEffect(() => {
    if (prompt.trim() && floatingPreviewOpen) {
      debouncedGeneratePreview(prompt, dimensions.width, dimensions.height, previewSeed);
    }
  }, [prompt, dimensions, previewSeed, floatingPreviewOpen, debouncedGeneratePreview]);

  // === Job handlers (same as Collage page) ===

  const handleSubmit = async (jobData: JobCreate) => {
    setIsSubmitting(true);
    try {
      await api.createJob(jobData);
      await fetchData();
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchSubmit = async (request: BatchJobCreate) => {
    const preview = await api.previewPermutations(request.prompt);
    if (preview.total_permutations > 20) {
      const confirmed = await confirm({
        title: 'Large Batch',
        message: `This will create ${preview.total_permutations} jobs. Continue?`,
        confirmText: 'Generate All',
        variant: 'warning',
      });
      if (!confirmed) return;
    }

    setIsSubmitting(true);
    try {
      const result = await api.generatePermutations(request);
      await fetchData();
      toast.success(`Created ${result.total_jobs} jobs from permutations`);
    } catch (error) {
      console.error('Failed to create batch jobs:', error);
      toast.error('Failed to create batch jobs: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelJob(jobId, force);
      await fetchData();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    // Optimistically remove from local state immediately
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
    try {
      await api.deleteJobPermanent(jobId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete job:', error);
      await fetchData(); // Restore correct state on failure
    }
  };

  const handleReuseFromJob = (job: JobResponse) => {
    setPrompt(job.prompt);
    setFormValues({
      prompt: job.prompt,
      width: job.width,
      height: job.height,
      steps: job.steps,
      cfg: job.cfg,
      seed: job.seed,
    });
    setSelectedJob(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');
  const completedJobs = jobs.filter(j => j.status === 'completed');

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar: bit library / agent chat tabs (overlay drawer below lg).
            Desktop is double width (lg:w-[40rem]): the library renders as two adjacent
            columns and the agent chat gets the full width. The drawer stays single-width;
            the library navigates between its columns in place there. */}
        <aside
          className={`${
            mobilePanel === 'library'
              ? 'fixed inset-y-0 left-0 z-50 flex flex-col w-80 max-w-[85vw] bg-[var(--bg-secondary)] shadow-2xl'
              : 'hidden'
          } lg:static lg:z-auto lg:max-w-none lg:shadow-none lg:flex lg:flex-col lg:w-[40rem] border-r border-[var(--border-subtle)] flex-shrink-0 bg-[var(--bg-secondary)] overflow-hidden`}
        >
          <div className="flex items-center border-b border-[var(--border-subtle)] flex-shrink-0">
            {(['library', 'agent'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setLeftTab(tab)}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors cursor-pointer border-b-2 ${
                  leftTab === tab
                    ? 'border-[var(--accent)] text-[var(--accent)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                {tab === 'library' ? 'Library' : 'Agent'}
              </button>
            ))}
            <button
              onClick={() => setMobilePanel(null)}
              className="lg:hidden px-3 py-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
              title="Close panel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Both panels stay mounted; hiding preserves chat history and panel state */}
          <div className={leftTab === 'library' ? 'flex-1 min-h-0' : 'hidden'}>
            <BitLibraryPanel
              categories={categories}
              bits={bits}
              onRefresh={fetchLibrary}
              onInsertBit={handleInsertBit}
              onInsertInlineList={handleInsertInlineList}
              activeBitIds={activeBitIds}
              onCastBit={handleCastBit}
            />
          </div>
          <div className={leftTab === 'agent' ? 'flex-1 min-h-0' : 'hidden'}>
            <AgentChatPanel
              getBuildState={getBuildState}
              onBuildOps={handleBuildOps}
              onLibraryChanged={fetchLibrary}
            />
          </div>
        </aside>

        {/* Main Content: prompt builder + gallery */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-6 py-4 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between gap-4">
              <h1 className="text-3xl font-semibold tracking-tight">Xplore</h1>
              <div className="flex items-center gap-2 shrink-0">
                {castingLabel && (
                  <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                    <svg className="w-3.5 h-3.5 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Casting {castingLabel}…
                  </span>
                )}
                {/* Drawer toggles — only below lg, where the asides are hidden */}
                <button
                  onClick={() => setMobilePanel('library')}
                  className="lg:hidden p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                  title="Open library / agent panel"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                </button>
                <button
                  onClick={() => setMobilePanel('settings')}
                  className="lg:hidden p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                  title="Open settings panel"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                  </svg>
                </button>
                <BuildsMenu prompt={prompt} onLoad={setPrompt} />
                <button
                  onClick={() => setFloatingPreviewOpen(prev => !prev)}
                  className={`p-2 rounded-lg transition-colors cursor-pointer ${
                    floatingPreviewOpen
                      ? 'text-[var(--accent)] bg-[var(--accent-soft)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                  }`}
                  title={floatingPreviewOpen ? 'Close floating preview' : 'Open floating preview'}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <rect x="2" y="3" width="20" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="12" y="10" width="10" height="10" rx="1" fill="currentColor" opacity={floatingPreviewOpen ? 0.3 : 0} strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10h10v10H12z" />
                  </svg>
                </button>
                <button
                  type="submit"
                  form={FORM_ID}
                  disabled={isSubmitting || !prompt.trim()}
                  className="btn btn-primary h-10 px-5 font-semibold"
                >
                  Generate
                </button>
              </div>
            </div>

            {/* Generation Form (controlled prompt; settings portal into the right sidebar) */}
            <BuilderForm
              formId={FORM_ID}
              key={JSON.stringify(formValues)}
              onSubmit={handleSubmit}
              onBatchSubmit={handleBatchSubmit}
              isLoading={isSubmitting}
              initialValues={formValues}
              series={series}
              selectedSeriesId={selectedSeriesId}
              onSeriesChange={setSelectedSeriesId}
              promptValue={prompt}
              onPromptChange={setPrompt}
              onDimensionsChange={handleDimensionsChange}
              onSeedChange={handleSeedChange}
              settingsHost={settingsHost}
              categories={categories}
              bitsBySlug={bitsBySlug}
              onSectionFocus={handleSectionFocus}
            />

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  In Progress
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {activeJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onCancel={handleCancel}
                      onSelect={setSelectedJob}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Gallery: all completed jobs of the selected series */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Gallery
                </h2>
                {completedJobs.length > 0 && (
                  <span className="text-xs text-[var(--text-muted)]">
                    {completedJobs.length} images
                  </span>
                )}
              </div>

              {completedJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <svg className="w-8 h-8 text-[var(--text-muted)] opacity-30 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <p className="text-xs text-[var(--text-muted)]">No images yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {completedJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onSelect={setSelectedJob}
                      onDelete={handleDeleteJob}
                      onCopy={handleReuseFromJob}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>

        {/* Right Sidebar: settings host only (BuilderForm portals its settings panel here).
            Below lg it becomes an overlay drawer — visibility toggles via classes only, so
            the host div (the portal target) never unmounts. */}
        <aside
          className={`${
            mobilePanel === 'settings'
              ? 'fixed inset-y-0 right-0 z-50 flex flex-col w-80 max-w-[85vw] bg-[var(--bg-secondary)] shadow-2xl'
              : 'hidden'
          } lg:static lg:z-auto lg:max-w-none lg:bg-transparent lg:shadow-none lg:flex lg:flex-col lg:w-80 border-l border-[var(--border-subtle)] flex-shrink-0 overflow-hidden`}
        >
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)] flex-shrink-0">
            <span className="text-sm font-medium">Settings</span>
            <button
              onClick={() => setMobilePanel(null)}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
              title="Close panel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div ref={setSettingsHost} className="flex-1 min-h-0 overflow-y-auto" />
        </aside>
      </div>

      {/* Mobile drawer backdrop */}
      {mobilePanel && (
        <div
          className="fixed inset-0 z-[45] bg-black/60 lg:hidden"
          onClick={() => setMobilePanel(null)}
        />
      )}

      {/* Floating Preview Window */}
      {floatingPreviewOpen && (
        <div
          className="fixed z-40 shadow-2xl rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] select-none"
          style={{
            left: floatingPos.x,
            top: floatingPos.y,
            width: floatingWidth,
          }}
        >
          {/* Title bar - drag handle */}
          <div
            className="flex items-center justify-between px-3 py-1.5 bg-[var(--bg-secondary)] border-b border-[var(--border-subtle)] cursor-move select-none"
            onMouseDown={(e) => {
              dragOffsetRef.current = {
                x: e.clientX - floatingPos.x,
                y: e.clientY - floatingPos.y,
              };
              setIsDragging(true);
              e.preventDefault();
            }}
          >
            <span className="text-xs font-medium text-[var(--text-muted)]">Preview</span>
            <button
              onClick={() => setFloatingPreviewOpen(false)}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              title="Close floating preview"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Preview content */}
          <div
            className="relative bg-[var(--bg-primary)]"
            style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }}
          >
            {previewImage ? (
              <img
                src={toDataUri(previewImage)}
                alt="Preview"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)]">
                <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            {/* Loading spinner overlay */}
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <svg className="w-6 h-6 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </div>
          {/* Resize handle - bottom right corner */}
          <div
            className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-0.5"
            onMouseDown={(e) => {
              resizeStartRef.current = {
                mouseX: e.clientX,
                mouseY: e.clientY,
                width: floatingWidth,
              };
              setIsResizing(true);
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <svg className="w-3 h-3 text-[var(--text-muted)] opacity-40" viewBox="0 0 12 12" fill="currentColor">
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="6" cy="10" r="1.5" />
              <circle cx="10" cy="6" r="1.5" />
              <circle cx="2" cy="10" r="1.5" />
              <circle cx="6" cy="6" r="1.5" />
              <circle cx="10" cy="2" r="1.5" />
            </svg>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedJob && selectedJob.status === 'completed' && (
        <ImageModal
          job={selectedJob}
          jobs={completedJobs}
          onClose={() => setSelectedJob(null)}
          onReuseParams={handleReuseFromJob}
          onDelete={handleDeleteJob}
          onNavigate={setSelectedJob}
          series={series}
          onSeriesChange={fetchData}
        />
      )}
    </div>
  );
}
